import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, ComputerMode } from "@rakazo/contracts";
import { isBuiltinToolName } from "@rakazo/contracts";
import {
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from "@rakazo/ui-web";
import { Wrench } from "lucide-react";
import { useId, useState } from "react";
import { toolbarButtonClass } from "./composer-toolbar";

/** Built-in tools grouped the way people think about them. Unknown names are ignored. */
const GROUPS = [
  {
    key: "computer",
    tools: [
      "computer_observe",
      "computer_act",
      "browser_navigate",
      "browser_snapshot",
      "browser_act",
      "open_path",
      "launch_app",
      "request_takeover",
    ],
  },
  { key: "files", tools: ["list_files", "read_file", "write_file", "attach_file", "shell"] },
  { key: "web", tools: ["web_search", "web_fetch"] },
  { key: "media", tools: ["render_plot", "generate_image", "generate_video"] },
  {
    key: "memory",
    tools: [
      "remember",
      "save_memory",
      "recall_memory",
      "forget_memory",
      "save_shared_memory",
      "search_history",
      "read_history",
    ],
  },
  {
    key: "apps",
    tools: ["connect_app", "add_mcp_server", "request_secret", "list_secrets", "secret_request", "forget_secret"],
  },
] as const;

type GroupKey = (typeof GROUPS)[number]["key"];

/**
 * Kommerce: per-bot tool switches and computer mode, from the composer.
 * Writes the same disabledBuiltinTools list the bot panel edits.
 */
export function ComposerToolsMenu({
  bot,
  disabled,
  onSaveTools,
  onSetComputer,
}: {
  bot: Bot;
  disabled?: boolean;
  onSaveTools: (disabledBuiltinTools: string[]) => Promise<void>;
  onSetComputer: (mode: ComputerMode) => Promise<void>;
}) {
  const { t } = useLingui();
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabledTools = bot.disabledBuiltinTools ?? [];

  const labels: Record<GroupKey, { title: string; hint: string }> = {
    computer: { title: t`Computer & browser`, hint: t`Use its own desktop and browser` },
    files: { title: t`Files & terminal`, hint: t`Read, write and run commands` },
    web: { title: t`Web search`, hint: t`Search and read web pages` },
    media: { title: t`Images & charts`, hint: t`Generate images, video and plots` },
    memory: { title: t`Memory`, hint: t`Save and recall facts` },
    apps: { title: t`Apps & secrets`, hint: t`Connect apps and use saved secrets` },
  };

  const groupTools = (key: GroupKey) =>
    (GROUPS.find((group) => group.key === key)?.tools ?? []).filter((name) => isBuiltinToolName(name));
  const groupOn = (key: GroupKey) => groupTools(key).some((name) => !disabledTools.includes(name));
  const offCount = GROUPS.filter((group) => !groupOn(group.key)).length;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch {
      setError(t`Could not save. Try again.`);
    } finally {
      setBusy(false);
    }
  }

  function toggle(key: GroupKey, on: boolean) {
    const tools = groupTools(key);
    const next = on
      ? disabledTools.filter((name) => !tools.includes(name as never))
      : [...new Set([...disabledTools, ...tools])];
    void run(() => onSaveTools(next));
  }

  return (
    <Popover>
      <PopoverTrigger
        aria-label={t`Tools`}
        disabled={disabled}
        className={toolbarButtonClass}
      >
        <Wrench size={14} strokeWidth={2} />
        <Trans>Tools</Trans>
        {offCount ? <span className="text-xs tabular-nums opacity-70">−{offCount}</span> : null}
      </PopoverTrigger>
      <PopoverContent align="end" side="top" className="w-72 gap-0 p-3">
        <div className="mb-2 text-sm font-medium text-foreground">
          <Trans>Tools for {bot.name}</Trans>
        </div>
        <div className="flex flex-col">
          {GROUPS.map((group) =>
            groupTools(group.key).length ? (
              <label
                key={group.key}
                htmlFor={`${ids}-${group.key}`}
                className="flex min-h-10 items-center justify-between gap-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block text-foreground">{labels[group.key].title}</span>
                  <span className="block text-xs text-muted-foreground">{labels[group.key].hint}</span>
                </span>
                <Switch
                  id={`${ids}-${group.key}`}
                  size="sm"
                  checked={groupOn(group.key)}
                  disabled={busy}
                  onCheckedChange={(checked) => toggle(group.key, checked)}
                />
              </label>
            ) : null,
          )}
        </div>
        <label
          htmlFor={`${ids}-computer-mode`}
          className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-3 text-sm"
        >
          <span className="text-foreground">
            <Trans>Computer</Trans>
          </span>
          <NativeSelect
            id={`${ids}-computer-mode`}
            className="h-8 w-40 text-xs"
            disabled={busy}
            value={bot.computerMode}
            onChange={(event) => void run(() => onSetComputer(event.target.value as ComputerMode))}
          >
            <NativeSelectOption value="team">{t`Shared team computer`}</NativeSelectOption>
            <NativeSelectOption value="dedicated">{t`Its own computer`}</NativeSelectOption>
          </NativeSelect>
        </label>
        {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
      </PopoverContent>
    </Popover>
  );
}
